import type {
  DictsysFrenchVerbInflectionScheme,
  DictsysInflectionSchemeCell,
  DictsysInflectionSchemeContent,
  DictsysInflectionSchemeFrenchVerbFutureTenses,
  DictsysInflectionSchemeFrenchVerbMood,
  DictsysInflectionSchemeFrenchVerbPerson,
  DictsysInflectionSchemeFrenchVerbTime,
  DictsysInflectionSchemeHeaderElement,
  DictsysInflectionSchemeTable
} from "@/types";
import { startsWithVowel } from "@/utils";

import { DictsysInflectionSchemeHelper } from ".";

type PersonType = keyof DictsysInflectionSchemeFrenchVerbPerson;
type MoodType = keyof DictsysInflectionSchemeFrenchVerbMood;

type IndicativeTime = "present" | "imperfect" | "simple_past" | "future";
const indicativeTimes: IndicativeTime[] = ["present", "imperfect", "simple_past", "future"];

type CompoundTime = "present_perfect" | "past_perfect" | "future_perfect";
const compoundTimes: CompoundTime[] = ["present_perfect", "past_perfect", "future_perfect"];

const pronounList: string[] = ["je/j'", "tu", "il/elle/on", "nous", "vous", "ils/elles"];
const subjonctifPronounList: string[] = [
  "que je/j'",
  "que tu",
  "qu'il/qu'elle/qu'on",
  "que nous",
  "que vous",
  "qu'ils/qu'elles"
];
const speaker: PersonType[] = [
  "first_person_singular",
  "second_person_singular",
  "third_person_he_singular",
  "first_person_plural",
  "second_person_plural",
  "third_person_plural"
];
interface Prefix {
  text: string;
  tooltip?: string;
}

function getIndicativePerson(
  prefix: Prefix,
  time: DictsysInflectionSchemeFrenchVerbTime,
  person: PersonType
): DictsysInflectionSchemeCell[] {
  const cells: DictsysInflectionSchemeCell[] = [];
  cells.push({ type: "sub-header", elements: [{ texts: [prefix.text], tooltip: prefix.tooltip }] });
  for (let i = 0; i < indicativeTimes.length; i++) {
    cells.push(DictsysInflectionSchemeHelper.getBodyCell(time[indicativeTimes[i]][person]));
  }
  return cells;
}

function getCompoundPerson(
  prefix: Prefix,
  future: DictsysInflectionSchemeFrenchVerbFutureTenses,
  person: PersonType
): DictsysInflectionSchemeCell[] {
  const cells: DictsysInflectionSchemeCell[] = [];
  cells.push({ type: "sub-header", elements: [{ texts: [prefix.text], tooltip: prefix.tooltip }] });
  for (let i = 0; i < compoundTimes.length; i++) {
    cells.push(DictsysInflectionSchemeHelper.getBodyCell(future[compoundTimes[i]][person]));
  }
  return cells;
}

function getSubjunctiveOrConditionalPerson(
  prefix: Prefix,
  time: DictsysInflectionSchemeFrenchVerbTime,
  person: PersonType
): DictsysInflectionSchemeCell[] {
  return [
    { type: "sub-header", elements: [{ texts: [prefix.text], tooltip: prefix.tooltip }] },
    DictsysInflectionSchemeHelper.getBodyCell(time["present"][person]),
    DictsysInflectionSchemeHelper.getBodyCell(time["past"][person])
  ];
}

function getImperativePerson(
  prefix: Prefix,
  time: DictsysInflectionSchemeFrenchVerbTime,
  person: PersonType
): DictsysInflectionSchemeCell[] {
  return [
    { type: "sub-header", elements: [{ texts: [prefix.text], tooltip: prefix.tooltip }] },
    DictsysInflectionSchemeHelper.getBodyCell(time["present"][person])
  ];
}

function getFutureProchePerson(
  prefix: Prefix,
  time: DictsysInflectionSchemeContent,
  person: PersonType
): DictsysInflectionSchemeCell[] {
  // Add inflected "aller" prefix
  const prefixes: Record<PersonType, string> = {
    first_person_singular: "vais",
    second_person_singular: "vas",
    third_person_he_singular: "va",
    first_person_plural: "allons",
    second_person_plural: "allez",
    third_person_plural: "vont"
  };

  // We need to clone the time object, because otherwise we trigger a recursive update loop in the Vue part
  const prefixedTime = { ...time, prefix: prefixes[person] };

  return [
    { type: "sub-header", elements: [{ texts: [prefix.text], tooltip: prefix.tooltip }] },
    DictsysInflectionSchemeHelper.getBodyCell([prefixedTime])
  ];
}

function getPasseRecentPerson(
  prefix: Prefix,
  time: DictsysInflectionSchemeContent,
  person: PersonType
): DictsysInflectionSchemeCell[] {
  // Add inflected "venir" prefix
  const lastWordOfPrefix = startsWithVowel(time.inflexion, "fr") ? "d'" : "de";
  const prefixes: Record<PersonType, string> = {
    first_person_singular: `viens ${lastWordOfPrefix}`,
    second_person_singular: `viens ${lastWordOfPrefix}`,
    third_person_he_singular: `vient ${lastWordOfPrefix}`,
    first_person_plural: `venons ${lastWordOfPrefix}`,
    second_person_plural: `venez ${lastWordOfPrefix}`,
    third_person_plural: `viennent ${lastWordOfPrefix}`
  };

  // We need to clone the time object, because otherwise we trigger a recursive update loop in the Vue part
  const prefixedTime = { ...time, prefix: prefixes[person] };

  return [
    { type: "sub-header", elements: [{ texts: [prefix.text], tooltip: prefix.tooltip }] },
    DictsysInflectionSchemeHelper.getBodyCell([prefixedTime])
  ];
}

function getTable(
  verb: DictsysInflectionSchemeFrenchVerbMood,
  mood: MoodType,
  separatorIndex = 3
): DictsysInflectionSchemeTable {
  const table: DictsysInflectionSchemeTable = { header: "", separatorIndex, cells: [] };
  const tooltip = 'Brug "j\'" når efterfølgende ord starter med vokaler eller stumt h';

  switch (mood) {
    case "indicative": {
      const time: DictsysInflectionSchemeFrenchVerbTime = verb[mood];

      table.header = "indicatif";
      table.cells = [
        [
          { type: "sub-header", elements: [{}] },
          { elements: [{ texts: ["presént"] }], type: "sub-header" },
          { elements: [{ texts: ["imparfait"] }], type: "sub-header" },
          { elements: [{ texts: ["passé simple"] }], type: "sub-header" },
          {
            elements: [{ texts: ["futur simple"] }],
            type: "sub-header",
            extraTable: {
              ...getTable(verb, "futur_proche", 2),
              text: 'Se "futur proche"',
              label: 'Gå til "futur proche"'
            }
          }
        ]
      ];
      for (let i = 0; i < pronounList.length; i++) {
        table.cells.push(
          getIndicativePerson({ text: pronounList[i], tooltip: i === 0 ? tooltip : undefined }, time, speaker[i])
        );
      }
      break;
    }

    case "perfect_tenses": {
      const tenses: DictsysInflectionSchemeFrenchVerbFutureTenses = verb[mood];

      table.header = "formes composées";
      table.extraTable = {
        ...getTable(verb, "passe_recent", 2),
        text: 'Se "passé récent"',
        label: 'Gå til "passé récent"'
      };
      table.cells = [
        [
          { elements: [{}], type: "sub-header" },
          { elements: [{ texts: ["passé composé"] }], type: "sub-header" },
          { elements: [{ texts: ["plus-que-parfait"] }], type: "sub-header" },
          { elements: [{ texts: ["futur antérieur"] }], type: "sub-header" }
        ]
      ];
      for (let i = 0; i < pronounList.length; i++) {
        table.cells.push(
          getCompoundPerson({ text: pronounList[i], tooltip: i === 0 ? tooltip : undefined }, tenses, speaker[i])
        );
      }
      break;
    }

    case "subjunctive": {
      const time: DictsysInflectionSchemeFrenchVerbTime = verb[mood];

      table.header = "subjonctif";
      table.cells = [
        [
          { elements: [{}], type: "sub-header" },
          { elements: [{ texts: ["présent"] }], type: "sub-header" },
          { elements: [{ texts: ["passé"] }], type: "sub-header" }
        ]
      ];
      for (let i = 0; i < pronounList.length; i++) {
        table.cells.push(
          getSubjunctiveOrConditionalPerson(
            { text: subjonctifPronounList[i], tooltip: i === 0 ? tooltip : undefined },
            time,
            speaker[i]
          )
        );
      }

      break;
    }

    case "conditional": {
      const time: DictsysInflectionSchemeFrenchVerbTime = verb[mood];

      table.header = "conditionnel";
      table.cells = [
        [
          { elements: [{}], type: "sub-header" },
          { elements: [{ texts: ["présent"] }], type: "sub-header" },
          { elements: [{ texts: ["passé"] }], type: "sub-header" }
        ]
      ];
      for (let i = 0; i < pronounList.length; i++) {
        table.cells.push(
          getSubjunctiveOrConditionalPerson(
            { text: pronounList[i], tooltip: i === 0 ? tooltip : undefined },
            time,
            speaker[i]
          )
        );
      }
      break;
    }
    case "imperative": {
      const time: DictsysInflectionSchemeFrenchVerbTime = verb[mood];

      table.header = "impératif";
      table.cells = [
        [
          { elements: [{}], type: "sub-header" },
          { elements: [{ texts: ["présent"] }], type: "sub-header" }
        ]
      ];
      for (let i = 0; i < pronounList.length; i++) {
        table.cells.push(
          getImperativePerson(
            { text: "(" + pronounList[i] + ")", tooltip: i === 0 ? tooltip : undefined },
            time,
            speaker[i]
          )
        );
      }
      break;
    }

    case "futur_proche": {
      const time = verb.infinitive.present[0];

      table.header = "futur proche";

      for (let i = 0; i < pronounList.length; i++) {
        table.cells.push(
          getFutureProchePerson({ text: pronounList[i], tooltip: i === 0 ? tooltip : undefined }, time, speaker[i])
        );
      }

      break;
    }

    case "passe_recent": {
      const time = verb.infinitive.present[0];

      table.header = "passé récent";

      for (let i = 0; i < pronounList.length; i++) {
        table.cells.push(
          getPasseRecentPerson({ text: pronounList[i], tooltip: i === 0 ? tooltip : undefined }, time, speaker[i])
        );
      }

      break;
    }

    default:
      break;
  }

  return table;
}

export function getHeaderElements(
  inflectionScheme: DictsysFrenchVerbInflectionScheme
): DictsysInflectionSchemeHeaderElement[] {
  if (!inflectionScheme.verb) {
    return [];
  }

  return [
    { type: "headWord", texts: [inflectionScheme.verb["infinitive"]["present"][0].index], tense: "infinitif" },
    { type: "inflection", texts: [inflectionScheme.verb.participle.present[0].index], tense: "participe présent" },
    { type: "inflection", texts: [inflectionScheme.verb.participle.past[0].index], tense: "participe passé" },
    { type: "inflection", texts: [inflectionScheme.verb.gerundium.present[0].index], tense: "Gérondif présent" }
  ];
}

export function getTables(inflectionScheme: DictsysFrenchVerbInflectionScheme): DictsysInflectionSchemeTable[] {
  if (!inflectionScheme.verb) {
    return [];
  }

  return [
    getTable(inflectionScheme.verb, "indicative"),
    getTable(inflectionScheme.verb, "perfect_tenses"),
    getTable(inflectionScheme.verb, "subjunctive"),
    getTable(inflectionScheme.verb, "conditional"),
    getTable(inflectionScheme.verb, "imperative")
  ];
}
